import { parameterUtils, type ModelData, type ModelParameter } from '@/shared/stores/model-utils'
import type { DimensionHandler } from '@/shared/stores/create-model-store'

const frameThickness = 60

function getParam(params: ModelParameter[], id: string) {
  return parameterUtils.findParameter(params, id)
}

function updateParam(model: ModelData, id: string, updates: Partial<ModelParameter>) {
  model.parameters = parameterUtils.updateParameterInArray(model.parameters!, id, updates)
}

function validateDimension(type: string, value: number): number {
  const rules: Record<string, { min: number; max: number }> = {
    totalWidth: { min: 2000, max: 10000 },
    totalHeight: { min: 1800, max: 8000 },
    leftSectionWidth: { min: 200, max: 2000 },
    doorLeftWidth: { min: 500, max: 2000 },
    doorRightWidth: { min: 500, max: 2500 },
    panelHeight: { min: 200, max: 3000 },
  }
  const rule = rules[type] || { min: 100, max: 10000 }
  return Math.max(rule.min, Math.min(rule.max, value))
}

function updateHingesProportionally(
  model: ModelData,
  frameId: string,
  oldFrameHeight: number,
  newFrameHeight: number,
) {
  const frame = getParam(model.parameters!, frameId)
  if (!frame?.hardware?.hinges) return

  const updatedHinges = frame.hardware.hinges.map((hinge) => {
    const isBottomHinge1 = hinge.positionId?.includes('bottom_hinge_1')
    const isBottomHinge2 = hinge.positionId?.includes('bottom_hinge_2')
    const relativeY = oldFrameHeight > 0 ? hinge.position.y / oldFrameHeight : 0
    let newY: number
    if (isBottomHinge1) {
      newY = Math.round(newFrameHeight * 0.75)
    } else if (isBottomHinge2) {
      newY = Math.round(newFrameHeight * 0.85)
    } else {
      newY = Math.max(60, Math.min(newFrameHeight - hinge.height - 60, relativeY * newFrameHeight))
    }
    return { ...hinge, position: { ...hinge.position, y: newY } }
  })

  updateParam(model, frameId, {
    hardware: { ...frame.hardware, hinges: updatedHinges },
  })
}

function updateHandlePosition(model: ModelData, frameId: string, newFrameHeight: number) {
  const frame = getParam(model.parameters!, frameId)
  if (!frame?.hardware?.handles) return

  const updatedHandles = frame.hardware.handles.map((handle, i) => {
    if (i === 0) {
      return { ...handle, position: { ...handle.position, y: Math.round(newFrameHeight * 0.53) } }
    }
    return handle
  })

  updateParam(model, frameId, {
    hardware: { ...frame.hardware, handles: updatedHandles },
  })
}

/**
 * Additive height adjustment for left section panels.
 * Left section has 3 stacked panels: top (fixed), middle (openable), bottom (fixed)
 * When one panel changes, the other two redistribute proportionally, keeping total height constant.
 */
function handleAdditiveHeightUpdate(model: ModelData, updateType: string, newHeight: number) {
  const params = model.parameters!

  const panel1 = getParam(params, 'left_glass_panel_top')
  const panel2 = getParam(params, 'left_glass_panel_opanable')
  const panel3 = getParam(params, 'left_glass_panel_bottom')
  const horizontal1 = getParam(params, 'middle_horizontal_frame_profile_1')
  const horizontal2 = getParam(params, 'middle_horizontal_frame_profile_2')

  if (!panel1 || !panel2 || !panel3 || !horizontal1 || !horizontal2) return

  const topMargin = 60
  const bottomMargin = 60
  const frameHeight = 60
  const currentModelHeight = getParam(params, 'left_frame_profile')?.height || 2200
  const totalGlassSpace = currentModelHeight - topMargin - bottomMargin - 4 * frameHeight

  const topWindowTopFrame = getParam(params, 'top_window_1_top_frame_profile')
  const topWindowBottomFrame = getParam(params, 'top_window_1_bottom_frame_profile')
  const windowTopFrameHeight = topWindowTopFrame?.height || 60
  const windowBottomFrameHeight = topWindowBottomFrame?.height || 60

  let newPanel1Height: number
  let newPanel2Height: number
  let newPanel3Height: number

  switch (updateType) {
    case 'leftPanel1Height':
    case 'leftPanelTopHeight': {
      const newPanel1GlassHeight = newHeight - topMargin - frameHeight
      const remainingSpace = totalGlassSpace - newPanel1GlassHeight
      if (remainingSpace <= 0) return
      const currentPanel2And3 = (panel2.height || 0) + (panel3.height || 0)
      if (currentPanel2And3 === 0) return
      const panel2Ratio = (panel2.height || 0) / currentPanel2And3
      const panel3Ratio = (panel3.height || 0) / currentPanel2And3
      newPanel1Height = newPanel1GlassHeight
      newPanel2Height = parseFloat((remainingSpace * panel2Ratio).toFixed(2))
      newPanel3Height = parseFloat((remainingSpace * panel3Ratio).toFixed(2))
      break
    }
    case 'leftPanel2Height':
    case 'leftPanelOpenableHeight': {
      const newPanel2GlassHeight = newHeight - windowTopFrameHeight - windowBottomFrameHeight
      const remainingSpace = totalGlassSpace - newPanel2GlassHeight
      if (remainingSpace <= 0) return
      const currentPanel1And3 = (panel1.height || 0) + (panel3.height || 0)
      if (currentPanel1And3 === 0) return
      const panel1Ratio = (panel1.height || 0) / currentPanel1And3
      const panel3Ratio = (panel3.height || 0) / currentPanel1And3
      newPanel1Height = parseFloat((remainingSpace * panel1Ratio).toFixed(2))
      newPanel2Height = newPanel2GlassHeight
      newPanel3Height = parseFloat((remainingSpace * panel3Ratio).toFixed(2))
      break
    }
    case 'leftPanel3Height':
    case 'leftPanelBottomHeight': {
      const newPanel3GlassHeight = newHeight - frameHeight - bottomMargin
      const remainingSpace = totalGlassSpace - newPanel3GlassHeight
      if (remainingSpace <= 0) return
      const currentPanel1And2 = (panel1.height || 0) + (panel2.height || 0)
      if (currentPanel1And2 === 0) return
      const panel1Ratio = (panel1.height || 0) / currentPanel1And2
      const panel2Ratio = (panel2.height || 0) / currentPanel1And2
      newPanel1Height = parseFloat((remainingSpace * panel1Ratio).toFixed(2))
      newPanel2Height = parseFloat((remainingSpace * panel2Ratio).toFixed(2))
      newPanel3Height = newPanel3GlassHeight
      break
    }
    default:
      return
  }

  // Update panel heights
  updateParam(model, 'left_glass_panel_top', { height: newPanel1Height })
  updateParam(model, 'left_glass_panel_opanable', { height: newPanel2Height })
  updateParam(model, 'left_glass_panel_bottom', { height: newPanel3Height })

  // Calculate new Y positions
  const newHorizontal1Y = parseFloat((topMargin + newPanel1Height).toFixed(2))
  const windowFrameY = parseFloat((newHorizontal1Y + frameHeight).toFixed(2))
  const newPanel2Y = parseFloat((windowFrameY + windowTopFrameHeight).toFixed(2))
  const newWindowHeight = parseFloat((windowTopFrameHeight + newPanel2Height + windowBottomFrameHeight).toFixed(2))
  const windowBottomFrameY = parseFloat((newPanel2Y + newPanel2Height).toFixed(2))
  const newHorizontal2Y = parseFloat((windowFrameY + newWindowHeight).toFixed(2))
  const newPanel3Y = parseFloat((newHorizontal2Y + frameHeight).toFixed(2))
  const bottomFrameY = parseFloat((newPanel3Y + newPanel3Height).toFixed(2))

  // Update positions
  updateParam(model, 'middle_horizontal_frame_profile_1', {
    position: { x: horizontal1.position.x, y: newHorizontal1Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    position: { x: 60, y: windowFrameY, z: 0 } as ModelParameter['position'],
  })

  const topWindowLeftFrame = getParam(params, 'top_window_1_left_frame_profile')
  updateParam(model, 'top_window_1_left_frame_profile', {
    position: { x: topWindowLeftFrame?.position?.x || 0, y: windowFrameY, z: 0 } as ModelParameter['position'],
    height: newWindowHeight,
  })

  const topWindowRightFrame = getParam(params, 'top_window_1_right_frame_profile')
  updateParam(model, 'top_window_1_right_frame_profile', {
    position: { x: topWindowRightFrame?.position?.x || 640, y: windowFrameY, z: 0 } as ModelParameter['position'],
    height: newWindowHeight,
  })

  updateHingesProportionally(model, 'top_window_1_left_frame_profile', topWindowLeftFrame?.height || newWindowHeight, newWindowHeight)
  updateHandlePosition(model, 'top_window_1_right_frame_profile', newWindowHeight)

  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: { x: 60, y: windowBottomFrameY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel_opanable', {
    position: { x: panel2.position.x, y: newPanel2Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    position: { x: horizontal2.position.x, y: newHorizontal2Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel_bottom', {
    position: { x: panel3.position.x, y: newPanel3Y, z: 0 } as ModelParameter['position'],
  })

  const bottomFrame = getParam(params, 'bottom_left_frame_profile')
  updateParam(model, 'bottom_left_frame_profile', {
    position: { x: bottomFrame?.position?.x || 0, y: bottomFrameY, z: 0 } as ModelParameter['position'],
  })
  const bottomCenterFrame = getParam(params, 'bottom_center_frame_profile_1')
  updateParam(model, 'bottom_center_frame_profile_1', {
    position: { x: bottomCenterFrame?.position?.x || 760, y: bottomFrameY, z: 0 } as ModelParameter['position'],
  })
}

const totalWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validatedValue = validateDimension('totalWidth', numValue)

  model.width = validatedValue
  updateParam(model, 'top_frame_profile', { width: validatedValue })
  updateParam(model, 'right_frame_profile', {
    position: { x: validatedValue - 60, y: 0, z: 0 } as ModelParameter['position'],
  })

  if (!cascadeUpdates) return model

  const ft = getParam(model.parameters!, 'top_frame_profile')?.height || 60
  const currentLeftWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 700
  const currentMiddleGlassPanelWidth = getParam(model.parameters!, 'middle_glass_panel')?.width || 1070
  const separatorWidth = getParam(model.parameters!, 'middle_vertical_frame_profile_center')?.width || 60
  const currentRightGlassPanelWidth = getParam(model.parameters!, 'right_glass_panel')?.width || 1650
  const rightFrameWidth = getParam(model.parameters!, 'right_frame_profile')?.width || 60

  const currentCenterSectionWidth = currentMiddleGlassPanelWidth + separatorWidth / 2
  const currentRightSectionWidth = separatorWidth / 2 + currentRightGlassPanelWidth + rightFrameWidth
  const currentTotalWidth = currentLeftWidth + currentCenterSectionWidth + currentRightSectionWidth

  const widthDifference = validatedValue - currentTotalWidth
  const perSectionAdjustment = Math.floor(widthDifference / 3)
  const remainder = widthDifference - perSectionAdjustment * 3

  const newLeftWidth = Math.max(200, currentLeftWidth + perSectionAdjustment)
  const newCenterSectionWidth = Math.max(500, currentCenterSectionWidth + perSectionAdjustment)
  const newRightSectionWidth = Math.max(500, currentRightSectionWidth + perSectionAdjustment + remainder)

  const newMiddleGlassPanelWidth = newCenterSectionWidth - separatorWidth / 2
  const newRightGlassPanelWidth = newRightSectionWidth - separatorWidth / 2 - rightFrameWidth

  // Left section
  updateParam(model, 'bottom_left_frame_profile', { width: newLeftWidth })
  const leftPanelWidth = newLeftWidth - ft * 2
  updateParam(model, 'left_glass_panel_top', { width: leftPanelWidth })
  updateParam(model, 'left_glass_panel_opanable', { width: leftPanelWidth - ft * 2 })
  updateParam(model, 'left_glass_panel_bottom', { width: leftPanelWidth })
  updateParam(model, 'middle_horizontal_frame_profile_1', { width: leftPanelWidth })
  updateParam(model, 'middle_horizontal_frame_profile_2', { width: leftPanelWidth })
  updateParam(model, 'top_window_1_top_frame_profile', { width: leftPanelWidth })
  updateParam(model, 'top_window_1_bottom_frame_profile', { width: leftPanelWidth })

  const leftSectionEnd = newLeftWidth + ft
  const middleEnd = leftSectionEnd + newMiddleGlassPanelWidth + ft

  updateParam(model, 'left_vertical_frame_profile', {
    position: { x: newLeftWidth - ft, y: 60, z: 0 } as ModelParameter['position'],
  })

  const windowFramesY = getParam(model.parameters!, 'top_window_1_left_frame_profile')?.position?.y || 733
  updateParam(model, 'top_window_1_left_frame_profile', {
    position: { x: ft, y: windowFramesY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_right_frame_profile', {
    position: { x: leftPanelWidth, y: windowFramesY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    position: { x: ft, y: windowFramesY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: {
      x: ft,
      y: getParam(model.parameters!, 'top_window_1_bottom_frame_profile')?.position?.y || 1407,
      z: 0,
    } as ModelParameter['position'],
  })

  // Middle section
  updateParam(model, 'middle_glass_panel', {
    width: newMiddleGlassPanelWidth,
    position: { x: leftSectionEnd - ft, y: ft, z: 0 } as ModelParameter['position'],
  })

  updateParam(model, 'middle_vertical_frame_profile_center', {
    position: { x: leftSectionEnd + newMiddleGlassPanelWidth - separatorWidth, y: ft, z: 0 } as ModelParameter['position'],
  })

  // Right section
  updateParam(model, 'right_glass_panel', {
    width: newRightGlassPanelWidth,
    position: { x: middleEnd - separatorWidth, y: ft, z: 0 } as ModelParameter['position'],
  })

  const totalCenterWidth = newMiddleGlassPanelWidth + ft + newRightGlassPanelWidth + rightFrameWidth
  updateParam(model, 'bottom_center_frame_profile_1', {
    width: totalCenterWidth,
    position: { x: leftSectionEnd - ft, y: (model.height as number) - ft, z: 0 } as ModelParameter['position'],
  })

  return model
}

const totalHeight: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const validatedValue = validateDimension('totalHeight', numValue)

  model.height = validatedValue

  updateParam(model, 'left_frame_profile', { height: validatedValue })
  updateParam(model, 'right_frame_profile', { height: validatedValue })
  updateParam(model, 'left_vertical_frame_profile', { height: validatedValue - 60 })
  updateParam(model, 'middle_vertical_frame_profile_center', { height: validatedValue - 120 })

  if (!cascadeUpdates) return model

  const glassHeight = validatedValue - 120
  updateParam(model, 'middle_glass_panel', { height: glassHeight })
  updateParam(model, 'right_glass_panel', { height: glassHeight })

  const currentPanel1 = getParam(model.parameters!, 'left_glass_panel_top')
  const currentPanel2 = getParam(model.parameters!, 'left_glass_panel_opanable')
  const currentPanel3 = getParam(model.parameters!, 'left_glass_panel_bottom')
  const horizontal1 = getParam(model.parameters!, 'middle_horizontal_frame_profile_1')
  const horizontal2 = getParam(model.parameters!, 'middle_horizontal_frame_profile_2')
  const topWindowTopFrame = getParam(model.parameters!, 'top_window_1_top_frame_profile')
  const topWindowBottomFrame = getParam(model.parameters!, 'top_window_1_bottom_frame_profile')

  const ft = 60

  const currentTotalHeight =
    ft + (currentPanel1?.height || 613) +
    (horizontal1?.height || 60) + (topWindowTopFrame?.height || 60) +
    (currentPanel2?.height || 614) + (topWindowBottomFrame?.height || 60) +
    (horizontal2?.height || 60) + (currentPanel3?.height || 613) + ft

  const heightDifference = validatedValue - currentTotalHeight
  const perSectionAdjustment = heightDifference / 3

  const currentF1SectionHeight = ft + (currentPanel1?.height || 613) + (horizontal1?.height || 60)
  const newF1SectionHeight = parseFloat((currentF1SectionHeight + perSectionAdjustment).toFixed(2))
  const newPanel1Height = parseFloat((newF1SectionHeight - ft - ft).toFixed(2))

  const currentA1SectionHeight = (topWindowTopFrame?.height || 60) + (currentPanel2?.height || 614) + (topWindowBottomFrame?.height || 60)
  const newA1SectionHeight = parseFloat((currentA1SectionHeight + perSectionAdjustment).toFixed(2))
  const newPanel2Height = parseFloat((newA1SectionHeight - (topWindowTopFrame?.height || 60) - (topWindowBottomFrame?.height || 60)).toFixed(2))

  const currentF2SectionHeight = (horizontal2?.height || 60) + (currentPanel3?.height || 613) + ft
  const newF2SectionHeight = parseFloat((currentF2SectionHeight + perSectionAdjustment).toFixed(2))
  const newPanel3Height = parseFloat((newF2SectionHeight - (horizontal2?.height || 60) - ft).toFixed(2))

  updateParam(model, 'left_glass_panel_top', { height: newPanel1Height })
  updateParam(model, 'left_glass_panel_opanable', { height: newPanel2Height })
  updateParam(model, 'left_glass_panel_bottom', { height: newPanel3Height })

  const horizontal1Y = ft + newPanel1Height
  const openableSectionY = horizontal1Y + ft
  const panel2Y = openableSectionY + (topWindowTopFrame?.height || 60)
  const horizontal2Y = openableSectionY + newA1SectionHeight
  const panel3Y = horizontal2Y + ft

  updateParam(model, 'middle_horizontal_frame_profile_1', {
    position: { x: horizontal1?.position?.x || 60, y: horizontal1Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_horizontal_frame_profile_2', {
    position: { x: horizontal2?.position?.x || 60, y: horizontal2Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel_opanable', {
    position: { x: currentPanel2?.position?.x || 120, y: panel2Y, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'left_glass_panel_bottom', {
    position: { x: currentPanel3?.position?.x || 60, y: panel3Y, z: 0 } as ModelParameter['position'],
  })

  const windowFrameHeight = (topWindowTopFrame?.height || 60) + newPanel2Height + (topWindowBottomFrame?.height || 60)
  updateParam(model, 'top_window_1_left_frame_profile', {
    height: windowFrameHeight,
    position: { x: ft, y: openableSectionY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_right_frame_profile', {
    height: windowFrameHeight,
    position: {
      x: getParam(model.parameters!, 'top_window_1_right_frame_profile')?.position?.x || 640,
      y: openableSectionY,
      z: 0,
    } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    position: { x: ft, y: openableSectionY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: { x: ft, y: openableSectionY + ft + newPanel2Height, z: 0 } as ModelParameter['position'],
  })

  updateHingesProportionally(model, 'top_window_1_left_frame_profile',
    getParam(model.parameters!, 'top_window_1_left_frame_profile')?.height || windowFrameHeight,
    windowFrameHeight)
  updateHandlePosition(model, 'top_window_1_right_frame_profile', windowFrameHeight)

  const bottomY = validatedValue - 60
  updateParam(model, 'bottom_left_frame_profile', {
    position: { x: 0, y: bottomY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'bottom_center_frame_profile_1', {
    position: {
      x: getParam(model.parameters!, 'bottom_center_frame_profile_1')?.position?.x || 700,
      y: bottomY,
      z: 0,
    } as ModelParameter['position'],
  })

  return model
}

const leftSectionWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const newLeftWidth = numValue

  const ft = getParam(model.parameters!, 'top_frame_profile')?.height || 60
  const currentMiddleGlassPanelWidth = getParam(model.parameters!, 'middle_glass_panel')?.width || 1070
  const separatorWidth = getParam(model.parameters!, 'middle_vertical_frame_profile_center')?.width || 60
  const currentRightGlassPanelWidth = getParam(model.parameters!, 'right_glass_panel')?.width || 1650
  const rightFrameWidth = getParam(model.parameters!, 'right_frame_profile')?.width || 60

  const newTotalWidth = newLeftWidth + currentMiddleGlassPanelWidth + separatorWidth + currentRightGlassPanelWidth + rightFrameWidth
  model.width = newTotalWidth

  updateParam(model, 'bottom_left_frame_profile', { width: newLeftWidth })
  updateParam(model, 'top_frame_profile', { width: newTotalWidth })

  const leftPanelWidth = newLeftWidth - ft * 2
  updateParam(model, 'left_glass_panel_top', { width: leftPanelWidth })
  updateParam(model, 'left_glass_panel_opanable', { width: leftPanelWidth - ft * 2 })
  updateParam(model, 'left_glass_panel_bottom', { width: leftPanelWidth })
  updateParam(model, 'middle_horizontal_frame_profile_1', { width: leftPanelWidth })
  updateParam(model, 'middle_horizontal_frame_profile_2', { width: leftPanelWidth })
  updateParam(model, 'top_window_1_top_frame_profile', { width: leftPanelWidth })
  updateParam(model, 'top_window_1_bottom_frame_profile', { width: leftPanelWidth })

  if (!cascadeUpdates) return model

  const leftSectionEnd = newLeftWidth + ft
  const middleEnd = leftSectionEnd + currentMiddleGlassPanelWidth + ft

  updateParam(model, 'left_vertical_frame_profile', {
    position: { x: newLeftWidth - ft, y: 60, z: 0 } as ModelParameter['position'],
  })

  const windowFramesY = getParam(model.parameters!, 'top_window_1_left_frame_profile')?.position?.y || 733
  updateParam(model, 'top_window_1_left_frame_profile', {
    position: { x: ft, y: windowFramesY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_right_frame_profile', {
    position: { x: leftPanelWidth, y: windowFramesY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_top_frame_profile', {
    position: { x: ft, y: windowFramesY, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'top_window_1_bottom_frame_profile', {
    position: {
      x: ft,
      y: getParam(model.parameters!, 'top_window_1_bottom_frame_profile')?.position?.y || 1407,
      z: 0,
    } as ModelParameter['position'],
  })

  updateParam(model, 'middle_glass_panel', {
    position: { x: leftSectionEnd - ft, y: ft, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'middle_vertical_frame_profile_center', {
    position: { x: leftSectionEnd + currentMiddleGlassPanelWidth - separatorWidth, y: ft, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    position: { x: middleEnd - separatorWidth, y: ft, z: 0 } as ModelParameter['position'],
  })

  const totalCenterWidth = currentMiddleGlassPanelWidth + ft + currentRightGlassPanelWidth + rightFrameWidth
  updateParam(model, 'bottom_center_frame_profile_1', {
    width: totalCenterWidth,
    position: { x: leftSectionEnd - ft, y: (model.height as number) - ft, z: 0 } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: newTotalWidth - ft, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const middleWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const newMiddleWidth = numValue

  const ft = getParam(model.parameters!, 'top_frame_profile')?.height || 60
  const currentLeftWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 700
  const separatorWidth = getParam(model.parameters!, 'middle_vertical_frame_profile_center')?.width || 60
  const currentRightGlassPanelWidth = getParam(model.parameters!, 'right_glass_panel')?.width || 1650
  const rightFrameWidth = getParam(model.parameters!, 'right_frame_profile')?.width || 60

  const newTotalWidth = currentLeftWidth + newMiddleWidth + separatorWidth + currentRightGlassPanelWidth + rightFrameWidth - separatorWidth / 2
  model.width = newTotalWidth

  updateParam(model, 'middle_glass_panel', { width: newMiddleWidth - separatorWidth / 2 })
  updateParam(model, 'top_frame_profile', { width: newTotalWidth })

  if (!cascadeUpdates) return model

  const leftSectionEnd = currentLeftWidth + ft
  const middleEnd = leftSectionEnd + newMiddleWidth + ft - separatorWidth / 2

  updateParam(model, 'middle_vertical_frame_profile_center', {
    position: { x: leftSectionEnd + newMiddleWidth - separatorWidth - separatorWidth / 2, y: ft, z: 0 } as ModelParameter['position'],
  })
  updateParam(model, 'right_glass_panel', {
    position: { x: middleEnd - separatorWidth, y: ft, z: 0 } as ModelParameter['position'],
  })

  const totalCenterWidth = newMiddleWidth + ft + currentRightGlassPanelWidth + rightFrameWidth - separatorWidth / 2
  updateParam(model, 'bottom_center_frame_profile_1', {
    width: totalCenterWidth,
    position: { x: leftSectionEnd - ft, y: (model.height as number) - ft, z: 0 } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: newTotalWidth - ft, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const rightWidth: DimensionHandler = (model, _initial, numValue, options) => {
  const { cascadeUpdates = true } = options
  const newRightWidth = numValue

  const ft = getParam(model.parameters!, 'top_frame_profile')?.height || 60
  const currentLeftWidth = getParam(model.parameters!, 'bottom_left_frame_profile')?.width || 700
  const separatorWidth = getParam(model.parameters!, 'middle_vertical_frame_profile_center')?.width || 60
  const currentMiddleGlassPanelWidth = getParam(model.parameters!, 'middle_glass_panel')?.width || 1070
  const rightFrameWidth = getParam(model.parameters!, 'right_frame_profile')?.width || 60

  const newTotalWidth = currentLeftWidth + currentMiddleGlassPanelWidth + newRightWidth + rightFrameWidth - separatorWidth / 2
  model.width = newTotalWidth

  updateParam(model, 'right_glass_panel', { width: newRightWidth - separatorWidth / 2 - rightFrameWidth })
  updateParam(model, 'top_frame_profile', { width: newTotalWidth })

  if (!cascadeUpdates) return model

  const leftSectionEnd = currentLeftWidth + ft
  const middleEnd = leftSectionEnd + currentMiddleGlassPanelWidth + ft - separatorWidth / 2

  updateParam(model, 'right_glass_panel', {
    position: { x: middleEnd - separatorWidth / 2, y: ft, z: 0 } as ModelParameter['position'],
  })

  const totalCenterWidth = currentMiddleGlassPanelWidth + newRightWidth + rightFrameWidth - separatorWidth / 2
  updateParam(model, 'bottom_center_frame_profile_1', {
    width: totalCenterWidth,
    position: { x: leftSectionEnd - ft, y: (model.height as number) - ft, z: 0 } as ModelParameter['position'],
  })

  updateParam(model, 'right_frame_profile', {
    position: { x: newTotalWidth - ft, y: 0, z: 0 } as ModelParameter['position'],
  })

  return model
}

const leftPanel1Height: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('panelHeight', numValue)
  handleAdditiveHeightUpdate(model, 'leftPanel1Height', validatedValue)
  return model
}

const leftPanel2Height: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('panelHeight', numValue)
  handleAdditiveHeightUpdate(model, 'leftPanel2Height', validatedValue)
  return model
}

const leftPanel3Height: DimensionHandler = (model, _initial, numValue) => {
  const validatedValue = validateDimension('panelHeight', numValue)
  handleAdditiveHeightUpdate(model, 'leftPanel3Height', validatedValue)
  return model
}

export const model17DimensionHandlers: Record<string, DimensionHandler> = {
  totalWidth,
  totalHeight,
  leftSectionWidth,
  middleWidth,
  rightWidth,
  leftPanel1Height,
  leftPanelTopHeight: leftPanel1Height,
  leftPanel2Height,
  leftPanelOpenableHeight: leftPanel2Height,
  leftPanel3Height,
  leftPanelBottomHeight: leftPanel3Height,
}
